import { useEffect, useState, useCallback } from "react";
import {
  detectLocale,
  setLocale as persistLocale,
  type Locale,
} from "@/locales/routeExplanations";

/**
 * React hook for the active UI locale.
 *
 * - Initial value comes from `detectLocale()`, which prefers a previously
 *   persisted choice (localStorage) over `navigator.language`.
 * - Subscribes to the `pleasant:locale-changed` window event so every
 *   consumer re-renders when any caller flips the locale via `setLocale`.
 * - The returned setter both persists the choice and notifies subscribers.
 */
export function useLocale(): [Locale, (next: Locale) => void] {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  useEffect(() => {
    const onChange = (e: Event) => {
      const next = (e as CustomEvent<Locale>).detail ?? detectLocale();
      setLocaleState(next);
    };
    // Cross-tab: storage event fires when another tab updates localStorage.
    const onStorage = (e: StorageEvent) => {
      if (e.key === "pleasant.locale.v1") setLocaleState(detectLocale());
    };
    window.addEventListener("pleasant:locale-changed", onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("pleasant:locale-changed", onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const update = useCallback((next: Locale) => {
    persistLocale(next);
    setLocaleState(next);
  }, []);

  return [locale, update];
}
